import type { Edge as ReactFlowEdge, Node as ReactFlowNode, XYPosition } from "@xyflow/react";

/** Current on-disk / IndexedDB data model version. */
export const KIMNOTE_SCHEMA_VERSION = 1;

export type EntityId = string;
export type ISODateString = string;

export type BuiltInNodeKind =
  | "note"
  | "text"
  | "heading"
  | "image"
  | "link"
  | "checklist"
  | "column"
  | "board"
  | "file"
  | "comment"
  | "table"
  | "sketch"
  | "unknown";

/**
 * Known node kinds retain autocomplete while third-party component kinds remain valid.
 */
export type NodeKind = BuiltInNodeKind | (string & {});

export interface ChecklistItem extends Record<string, unknown> {
  id: EntityId;
  text: string;
  checked: boolean;
}

/**
 * Persisted component data. Extra fields are deliberately accepted and round-tripped so
 * a project can be opened even when the component plug-in is not installed.
 */
export interface CanvasNodeData extends Record<string, unknown> {
  kind: NodeKind;
  title?: string;
  content?: string;
  color?: string;
  /** Optional node-level override; omitted nodes follow the current canvas default. */
  borderRadius?: number;
  /** Notes grow with their Markdown content unless this is explicitly disabled. */
  autoHeight?: boolean;
  items?: ChecklistItem[];
  assetHash?: string;
  url?: string;
  fileName?: string;
  targetBoardId?: EntityId;
  props?: Record<string, unknown>;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  pluginVersion: number;
}

export interface CanvasEdgeData extends Record<string, unknown> {
  color?: string;
  label?: string;
  createdAt?: ISODateString;
  updatedAt?: ISODateString;
}

/** React Flow view models used by the canvas layer. */
export type CanvasNode = ReactFlowNode<CanvasNodeData, "canvasNode">;
export type CanvasEdge = ReactFlowEdge<CanvasEdgeData, "canvasEdge">;

export interface Viewport {
  x: number;
  y: number;
  zoom: number;
}

export const DEFAULT_VIEWPORT: Readonly<Viewport> = Object.freeze({
  x: 0,
  y: 0,
  zoom: 1,
});

export interface ProjectRecord {
  id: EntityId;
  title: string;
  schemaVersion: number;
  activeBoardId: EntityId;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  lastOpenedAt: ISODateString;
}

export type Project = ProjectRecord;

export interface BoardRecord {
  id: EntityId;
  projectId: EntityId;
  parentId: EntityId | null;
  title: string;
  order: number;
  viewport: Viewport;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export type Board = BoardRecord;

/**
 * Compact IndexedDB representation. Runtime-only React Flow flags such as selected,
 * dragging and measured are intentionally excluded.
 */
export interface StoredNode {
  id: EntityId;
  boardId: EntityId;
  type: "canvasNode";
  position: XYPosition;
  width?: number;
  height?: number;
  zIndex: number;
  parentId?: EntityId;
  hidden?: boolean;
  draggable?: boolean;
  selectable?: boolean;
  connectable?: boolean;
  deletable?: boolean;
  data: CanvasNodeData;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export interface StoredEdge {
  id: EntityId;
  boardId: EntityId;
  type: "canvasEdge";
  source: EntityId;
  target: EntityId;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  animated?: boolean;
  hidden?: boolean;
  data?: CanvasEdgeData;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export interface AssetRecord {
  /** Lower-case SHA-256 hex digest and content-addressed primary key. */
  hash: string;
  blob: Blob;
  mimeType: string;
  byteLength: number;
  originalName?: string;
  createdAt: ISODateString;
}

export interface AssetRefRecord {
  projectId: EntityId;
  hash: string;
}

export interface SettingRecord<T = unknown> {
  key: string;
  value: T;
}

export interface BoardSnapshot {
  project: ProjectRecord;
  board: BoardRecord;
  nodes: StoredNode[];
  edges: StoredEdge[];
}

export function nowIso(): ISODateString {
  return new Date().toISOString();
}

export function createEntityId(prefix?: string): EntityId {
  const randomId = globalThis.crypto?.randomUUID?.() ?? fallbackRandomId();
  return prefix ? `${prefix}_${randomId}` : randomId;
}

function fallbackRandomId(): string {
  const random = Math.random().toString(36).slice(2);
  return `${Date.now().toString(36)}-${random}`;
}

export function canvasNodeToStoredNode(node: CanvasNode, boardId: EntityId): StoredNode {
  const createdAt = node.data.createdAt;
  const updatedAt = nowIso();
  return {
    id: node.id,
    boardId,
    type: "canvasNode",
    position: { x: node.position.x, y: node.position.y },
    width: node.width ?? node.initialWidth,
    height: node.height ?? node.initialHeight,
    zIndex: node.zIndex ?? 0,
    parentId: node.parentId,
    hidden: node.hidden,
    draggable: node.draggable,
    selectable: node.selectable,
    connectable: node.connectable,
    deletable: node.deletable,
    data: node.data,
    createdAt,
    updatedAt,
  };
}

export function storedNodeToCanvasNode(node: StoredNode): CanvasNode {
  return {
    id: node.id,
    type: "canvasNode",
    position: { x: node.position.x, y: node.position.y },
    width: node.width,
    height: node.height,
    zIndex: node.zIndex,
    parentId: node.parentId,
    hidden: node.hidden,
    draggable: node.draggable,
    selectable: node.selectable,
    connectable: node.connectable,
    deletable: node.deletable,
    data: node.data,
  };
}

export function canvasEdgeToStoredEdge(edge: CanvasEdge, boardId: EntityId): StoredEdge {
  const timestamp = nowIso();
  return {
    id: edge.id,
    boardId,
    type: "canvasEdge",
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    animated: edge.animated,
    hidden: edge.hidden,
    data: edge.data,
    createdAt: edge.data?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };
}

export function storedEdgeToCanvasEdge(edge: StoredEdge): CanvasEdge {
  return {
    id: edge.id,
    type: "canvasEdge",
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    animated: edge.animated,
    hidden: edge.hidden,
    data: edge.data,
  };
}
